import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Star, Sparkles, Heart, ExternalLink } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost12: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateBoyName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-boy-names', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  const boyNamesByMeaning = [
    {
      category: 'Wisdom & Intelligence',
      names: [
        { chinese: '文博', pinyin: 'Wénbó', meaning: 'Cultured and learned' },
        { chinese: '智渊', pinyin: 'Zhìyuān', meaning: 'Wise and profound' },
        { chinese: '睿泽', pinyin: 'Ruìzé', meaning: 'Wise and benevolent' },
        { chinese: '书豪', pinyin: 'Shūháo', meaning: 'Scholarly and outstanding' }
      ]
    },
    {
      category: 'Strength & Power',
      names: [
        { chinese: '刚毅', pinyin: 'Gāngyì', meaning: 'Strong and resolute' },
        { chinese: '威龙', pinyin: 'Wēilóng', meaning: 'Mighty dragon' },
        { chinese: '豪杰', pinyin: 'Háojié', meaning: 'Heroic and outstanding' },
        { chinese: '霸天', pinyin: 'Bàtiān', meaning: 'Dominate the heavens' }
      ]
    },
    {
      category: 'Virtue & Character',
      names: [
        { chinese: '仁义', pinyin: 'Rényì', meaning: 'Benevolent and righteous' },
        { chinese: '正直', pinyin: 'Zhèngzhí', meaning: 'Upright and honest' },
        { chinese: '诚信', pinyin: 'Chéngxìn', meaning: 'Sincere and trustworthy' },
        { chinese: '浩然', pinyin: 'Hàorán', meaning: 'Vast and upright' }
      ]
    }
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Boy Names and Meanings - Deep Dive | Complete Guide"
        description="Want to understand what Chinese boy names really mean? Deep dive into powerful male names like 浩然 (vast and upright), 子轩 (noble son), and 宇航 (cosmic navigator). Learn how characters combine to create meaningful names, explore traditional vs modern trends, and discover names that convey strength, wisdom, and virtue. Complete guide with character breakdowns, cultural context, and pronunciation. Perfect for parents choosing meaningful names for sons."
        keywords="chinese boy names and meanings, chinese male names meaning, traditional chinese boy names, powerful chinese male names, chinese boy names with good meaning, chinese boy name meanings explained"
        pathname="/blog/chinese-boy-names-meanings"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Boy Names Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Chinese Boy Names and Meanings: Deep Dive
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Explore the profound meanings behind Chinese boy names. This comprehensive guide delves into traditional, modern, and powerful male Chinese names with detailed cultural context and character explanations.
            </p>
          </div>
        </header>

        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Chinese Boy Names</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Chinese boy names and meanings</strong> carry deep cultural significance, often reflecting virtues, aspirations, and traditional values. Each character in a Chinese name contributes to its overall meaning, creating names that are both beautiful and meaningful. Exploring <strong>Chinese boy names and meanings</strong> reveals the values and aspirations important in Chinese culture.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Unlike Western names, <strong>Chinese boy names</strong> are carefully chosen to convey specific qualities parents hope their sons will embody. From traditional virtues like wisdom and integrity to modern aspirations like success and innovation, <strong>Chinese boy names and meanings</strong> tell a story of cultural values and personal hopes.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Understanding <strong>Chinese boy names and meanings</strong> helps parents choose names that reflect their values while honoring Chinese cultural traditions. Each <strong>Chinese boy name</strong> carries layers of meaning that connect the individual to Chinese history and culture.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cultural Context of Boy Names</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Chinese boy names and meanings</strong> are deeply rooted in Chinese cultural values:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li><strong>Confucian values:</strong> Many <strong>Chinese boy names</strong> emphasize virtues like wisdom, integrity, and filial piety</li>
              <li><strong>Strength and leadership:</strong> <strong>Chinese boy names and meanings</strong> often reflect qualities of strength, courage, and leadership</li>
              <li><strong>Scholarly achievement:</strong> Education and learning are common themes in <strong>Chinese boy names</strong></li>
              <li><strong>Nature and elements:</strong> Some <strong>Chinese boy names and meanings</strong> draw from natural elements like mountains, rivers, or celestial bodies</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              When exploring <strong>Chinese boy names and meanings</strong>, it's important to understand how these names reflect broader cultural values and historical traditions.
            </p>
          </div>

          {boyNamesByMeaning.map((section, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{section.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {section.names.map((name, nameIndex) => (
                  <div key={nameIndex} className="border-l-4 border-blue-500 pl-4">
                    <div className="text-2xl font-bold text-gray-900 mb-1">{name.chinese}</div>
                    <div className="text-blue-600 font-medium mb-2">{name.pinyin}</div>
                    <p className="text-gray-600">{name.meaning}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Your Perfect Chinese Boy Name</h2>
            <p className="text-gray-700 mb-6">
              Want a personalized Chinese boy name? Use our AI-powered generator to create authentic names based on your preferences.
            </p>
            <form onSubmit={handleGenerateBoyName} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Generate Chinese Boy Name →
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/chinese-boy-names" className="text-blue-600 hover:underline font-medium">
                  Browse 200+ Chinese Boy Names →
                </Link>
              </li>
              <li>
                <Link to="/blog/chinese-names-meanings" className="text-blue-600 hover:underline font-medium">
                  Chinese Names and Meanings Guide →
                </Link>
              </li>
              <li>
                <Link to="/" className="text-blue-600 hover:underline font-medium">
                  Chinese Name Generator →
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost12;

